import {classifyPlaybackFailure, extractHttpStatus} from 'utils/playerPlayback';

/** Desired real-media cushion before initial playback. */
export const TARGET_BUFFER_SECONDS = 10;
/** Accept a smaller cushion when a live playlist cannot expose ten seconds. */
export const MIN_DEGRADED_BUFFER_SECONDS = 2;
/** Time spent trying to reach the full target before accepting the minimum. */
export const TARGET_BUFFER_WAIT_MS = 12000;
/** A source that has produced no useful buffer by this point is restarted. */
export const EMPTY_BUFFER_TIMEOUT_MS = 20000;
/** Cushion required before removing the recovery overlay. */
export const RECOVERY_BUFFER_SECONDS = 4;
/** Frozen playback beyond this point recreates the HLS session. */
export const PLAYBACK_STALL_TIMEOUT_MS = 15000;
export const BASE_RESTART_DELAY_MS = 1000;
export const MAX_RESTART_DELAY_MS = 15000;
export const HEALTHY_RESET_MS = 30000;
/** Minimum playhead movement (either direction) that counts as progress. */
export const PROGRESS_EPSILON_SECONDS = 0.04;
/** A backward jump beyond this is a timeline reset (discontinuity/re-anchor), still progress. */
export const TIMELINE_RESET_TOLERANCE_SECONDS = 1;
/** Stall budget while the relay itself reports that it is recovering. */
export const RELAY_RECOVERY_STALL_TIMEOUT_MS = 60000;
export const RELAY_RECOVERY_STATES = Object.freeze(['waiting', 'starting', 'recovering']);
/** hls.js in-place media recoveries before a full generation restart. */
export const MEDIA_RECOVERY_MAX_ATTEMPTS = 2;
/** hls.js in-place reloads (startLoad) for fragment/key failures before a restart. */
export const INPLACE_RELOAD_MAX_ATTEMPTS = 2;
/** Immediate restarts requested within this window of a fresh transport are coalesced. */
export const RESTART_COALESCE_MS = BASE_RESTART_DELAY_MS;
/** A restart does not resume further behind the live edge than this. */
export const RESUME_MAX_BEHIND_SECONDS = 24;

export function computeUltraBackoffDelay(attempt) {
  const n = Math.max(1, Math.floor(attempt) || 1);
  return Math.min(BASE_RESTART_DELAY_MS * Math.pow(2, n - 1), MAX_RESTART_DELAY_MS);
}

export function nextUltraAttempt(currentAttempt, healthyPlaybackMs) {
  if (healthyPlaybackMs >= HEALTHY_RESET_MS) return 1;
  return (currentAttempt || 0) + 1;
}

/**
 * Return playable seconds ahead of the current position. During HLS startup
 * currentTime may still be zero while the first range uses media timestamps,
 * so a wholly-future range is measured from its own start.
 */
export function bufferedSecondsAhead(buffered, currentTime = 0) {
  if (!buffered || typeof buffered.length !== 'number') return 0;

  let best = 0;
  for (let index = 0; index < buffered.length; index += 1) {
    const start = Number(buffered.start(index));
    const end = Number(buffered.end(index));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;

    if (currentTime >= start && currentTime <= end) {
      best = Math.max(best, end - currentTime);
    } else if (currentTime < start) {
      best = Math.max(best, end - start);
    }
  }
  return best;
}

/**
 * Decide when initial playback may begin. The degraded path avoids waiting
 * forever on short live windows while still preferring a ~10 second cushion.
 */
export function startupBufferDecision(bufferedSeconds, elapsedMs) {
  if (bufferedSeconds >= TARGET_BUFFER_SECONDS) return 'ready';
  if (elapsedMs >= TARGET_BUFFER_WAIT_MS && bufferedSeconds >= MIN_DEGRADED_BUFFER_SECONDS) {
    return 'degraded';
  }
  if (elapsedMs >= EMPTY_BUFFER_TIMEOUT_MS) return 'restart';
  return 'wait';
}

/**
 * Keep rolling-deploy compatibility with an older backend that returned raw
 * MPEG-TS, but never interpret a retryable 4xx/5xx control-plane response as
 * media. Current Player C deliberately stays on HLS during relay recovery.
 */
export function shouldUseCompatibleMpegts(fatal, manifestParsed, statusCode = null) {
  const numericStatus = Number(statusCode);
  // Raw MPEG-TS compatibility is only plausible when the request itself
  // succeeded and the response then failed HLS parsing. Missing status is a
  // transport/DNS/CORS/timeout signal and must remain on HLS backoff.
  const isSuccessfulMediaResponse =
    Number.isFinite(numericStatus) && numericStatus >= 200 && numericStatus < 300;
  return Boolean(fatal && !manifestParsed && isSuccessfulMediaResponse);
}

/**
 * Playhead progress for the stall watchdog. Any movement beyond the epsilon
 * counts, including backwards: hls.js re-anchors the timeline after a media
 * recovery or discontinuity and the old high-water mark must not turn that
 * into a false "frozen picture" restart.
 */
export function evaluatePlayheadProgress(previousTime, currentTime) {
  const current = Number(currentTime);
  if (!Number.isFinite(current)) return {progressed: false, reset: false};
  const previous = Number(previousTime);
  if (!Number.isFinite(previous) || previous < 0) return {progressed: true, reset: false};
  const delta = current - previous;
  return {
    progressed: Math.abs(delta) > PROGRESS_EPSILON_SECONDS,
    reset: delta < -TIMELINE_RESET_TOLERANCE_SECONDS
  };
}

/** Stall budget: longer while a fresh relay sample says the relay is recovering. */
export function stallBudgetMs(relayState, relayFresh) {
  return relayFresh && RELAY_RECOVERY_STATES.includes(relayState)
    ? RELAY_RECOVERY_STALL_TIMEOUT_MS
    : PLAYBACK_STALL_TIMEOUT_MS;
}

const MANIFEST_OR_LEVEL_DETAILS = /^(manifest|level)/i;
const FRAGMENT_OR_KEY_DETAILS = /^(frag|key)/i;

/**
 * Decide what to do with an hls.js ERROR payload. HTTP status codes only
 * classify manifest/level failures: a fragment 404 means the relay evicted an
 * old segment, never that the channel disappeared, so it stays retryable and
 * is first handled in place (startLoad) without replacing the session.
 */
export function hlsErrorAction(
  data,
  {manifestParsed = false, mediaRecoveryAttempts = 0, inplaceReloadAttempts = 0} = {},
  compatibleCheck = shouldUseCompatibleMpegts
) {
  const details = String(data?.details || '');
  const fallback = details.toLowerCase().includes('buffer') ? 'playbackStall' : 'network';
  const classification = MANIFEST_OR_LEVEL_DETAILS.test(details)
    ? classifyPlaybackFailure(data, fallback)
    : {retryable: true, reason: fallback, statusCode: extractHttpStatus(data)};

  if (compatibleCheck(data?.fatal, manifestParsed, classification.statusCode)) {
    return {action: 'compatible', classification};
  }
  if (!classification.retryable) return {action: 'terminal', classification};
  if (!data?.fatal) {
    return {action: fallback === 'playbackStall' ? 'buffering' : 'ignore', classification};
  }
  if (data.type === 'mediaError' && mediaRecoveryAttempts < MEDIA_RECOVERY_MAX_ATTEMPTS) {
    return {action: 'recoverMedia', classification};
  }
  if (
    data.type === 'networkError' &&
    manifestParsed &&
    FRAGMENT_OR_KEY_DETAILS.test(details) &&
    inplaceReloadAttempts < INPLACE_RELOAD_MAX_ATTEMPTS
  ) {
    return {action: 'reloadInPlace', classification};
  }
  return {action: 'restart', classification};
}

/**
 * Position to resume at after a restart of the same relay session: the
 * fragment with the previously playing media sequence plus the offset inside
 * it. -1 (hls.js live sync) when the sequence left the window.
 */
export function resumePositionForSequence(details, resume) {
  const fragments = details?.fragments;
  if (!resume || !Array.isArray(fragments) || fragments.length === 0) return -1;
  const fragment = fragments.find((candidate) => candidate?.sn === resume.sn);
  if (!fragment || !Number.isFinite(fragment.start)) return -1;
  const last = fragments[fragments.length - 1];
  const edge = Number.isFinite(details.edge)
    ? details.edge
    : Number(last.start) + Number(last.duration || 0);
  const offset = Number.isFinite(resume.offset) ? Math.max(0, resume.offset) : 0;
  const target = fragment.start + Math.min(offset, Number(fragment.duration) || 0);
  if (!Number.isFinite(edge) || target >= edge - 1) return -1;
  if (edge - target > RESUME_MAX_BEHIND_SECONDS) return -1;
  return target;
}
